import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { sendChatMessage, listChatConversations, createChatConversation, deleteChatConversation, getChatMessages } from "@/api/chat";
import type { ChatIntent } from "@/api/chat";
import { listSessions } from "@/api/alpha";
import ActionButtons, { stripActions } from "@/components/feature/ActionButtons";
import { useSpeechRecognition } from "@/hooks/useSpeechRecognition";

const quickQuestions = ["宁德时代现在怎么看?", "我的持仓今天怎么样?", "目前推荐什么板块?", "今天大盘分析一下"];

interface Conversation {
  id: number;
  title: string;
  createdAt: number;
}

export default function AITrader() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<{role:string;content:string;id:number}[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [showQuick, setShowQuick] = useState(true);
  const bottomRef = useRef<HTMLDivElement>(null);
  const nextId = useRef(1);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [conversationId, setConversationId] = useState<number>(0);
  const [showSidebar, setShowSidebar] = useState(false);
  const [convTitle, setConvTitle] = useState("");
  const [latestIntent, setLatestIntent] = useState<ChatIntent | null>(null);

  const {
    isListening: voiceListening,
    transcript: voiceTranscript,
    startListening: startVoice,
    stopListening: stopVoice,
    isSupported: voiceSupported,
  } = useSpeechRecognition();

  // 加载对话列表 + 自动选中最新对话
  const loadConversations = useCallback(() => {
    listChatConversations().then((r: any) => {
      const list = r?.data || r || [];
      setConversations(list);
      // 自动选中最新对话，直接加载历史消息到对话框
      if (list.length > 0 && conversationId === 0) {
        const conv = list[0];
        setConversationId(conv.id);
        setShowQuick(false);
        setConvTitle(conv.title);
        nextId.current = 1;
        getChatMessages(conv.id).then((r2: any) => {
          const msgs = r2?.data || r2 || [];
          const mapped = msgs.map((m: any) => ({
            id: nextId.current++,
            role: m.role === "assistant" ? ("ai" as const) : (m.role as "user"),
            content: m.content,
          }));
          setMessages(mapped);
        }).catch(() => {});
      }
    }).catch(() => {});
  }, []);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  useEffect(() => {
    listSessions().then(({ sessions }) => {
      const s = sessions.find((x: any) => x.status === "running") || sessions[0];
      if (s) setSessionId(s.id);
    }).catch(() => {});
  }, []);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  // ── 语音识别结果自动填入输入框 ──
  useEffect(() => {
    if (voiceListening && voiceTranscript) setInput(voiceTranscript);
  }, [voiceListening, voiceTranscript]);

  const handleVoiceToggle = () => {
    if (voiceListening) {
      stopVoice();
      if (voiceTranscript.trim()) {
        const text = voiceTranscript.trim();
        setInput("");
        setTimeout(() => handleSend(text), 200);
      }
    } else {
      setInput("");
      startVoice();
    }
  };

  // 切换对话
  const selectConversation = async (conv: Conversation) => {
    setConversationId(conv.id);
    setShowSidebar(false);
    setMessages([]);
    setShowQuick(false);
    setConvTitle(conv.title);
    nextId.current = 1;
    try {
      const r = await getChatMessages(conv.id);
      const msgs = (r as any)?.data || r || [];
      const mapped = msgs.map((m: any, i: number) => ({
        id: nextId.current++,
        role: m.role === "assistant" ? "ai" : m.role,
        content: m.content,
      }));
      setMessages(mapped);
    } catch { /* ignore */ }
  };

  // 新建对话
  const newConversation = async () => {
    try {
      const r = await createChatConversation();
      const conv = (r as any)?.data || r;
      setConversationId(conv.id);
      setConvTitle("");
      setMessages([]);
      setShowQuick(true);
      setShowSidebar(false);
      nextId.current = 1;
      loadConversations();
    } catch { /* ignore */ }
  };

  // 删除对话
  const handleDelete = async (id: number) => {
    try {
      await deleteChatConversation(id);
      if (conversationId === id) {
        setConversationId(0);
        setMessages([]);
        setShowQuick(true);
      }
      loadConversations();
    } catch { /* ignore */ }
  };

  const handleSend = async (text?: string) => {
    const content = (text || input).trim();
    if (!content || loading) return;
    setShowQuick(false);

    // 如果是新对话，自动创建 conversation
    let cid = conversationId;
    if (cid === 0) {
      try {
        const r = await createChatConversation();
        const conv = (r as any)?.data || r;
        cid = conv.id;
        setConversationId(cid);
        loadConversations();
      } catch { /* ignore */ }
    }

    const userMsg = { id: nextId.current++, role: "user" as const, content };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    const aiId = nextId.current++;
    setMessages((prev) => [...prev, { id: aiId, role: "ai", content: "" }]);
    setLoading(true);
    setLatestIntent(null); // 清除上轮意图
    let acc = "";
    try {
      const history = [...messages, userMsg].map((m) => ({ role: m.role === "ai" ? "assistant" as const : "user" as const, content: m.content }));
      for await (const delta of sendChatMessage(history, sessionId, cid, {
        onIntent: (intent) => setLatestIntent(intent),
      })) {
        acc += delta;
        setMessages((prev) => prev.map((m) => m.id === aiId ? { ...m, content: acc } : m));
      }
      setMessages((prev) => prev.map((m) => m.id === aiId ? { ...m, content: acc || "暂时无法回答，请稍后再试。" } : m));
      if (acc && cid) {
        // 用第一条消息重命名对话
        setConvTitle(content.slice(0, 30));
        loadConversations();
      }
    } catch {
      setMessages((prev) => prev.map((m) => m.id === aiId ? { ...m, content: "AI 暂时无法响应，请稍后再试。" } : m));
    } finally { setLoading(false); }
  };

  return (
    <div className="absolute inset-x-0 top-0 bottom-0 max-sm:bottom-[80px] flex flex-col overflow-hidden bg-white">
      {/* Header */}
      <div className="shrink-0 px-4 pt-4 pb-2 flex items-center gap-2">
        <button onClick={() => setShowSidebar(true)}
          className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center cursor-pointer shrink-0">
          <i className="ri-menu-line text-foreground-600 text-sm" />
        </button>
        <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center"><i className="ri-robot-2-fill text-primary-500 text-sm" /></div>
        <div className="min-w-0 flex-1">
          <h1 className="text-base font-bold text-foreground-950 truncate">{convTitle || "AI 助手"}</h1>
          <span className="text-[10px] text-foreground-400">模拟分析 · 仅供学习参考</span>
        </div>
        <button onClick={() => navigate('/ai-trader/memory')}
          className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center cursor-pointer shrink-0" title="AI 对我的了解">
          <i className="ri-brain-line text-foreground-600 text-sm" />
        </button>
      </div>

      {/* Chat area */}
      <div className="flex-1 overflow-y-auto min-h-0 px-4 py-2">
        {showQuick && (
          <div className="mb-4">
            <p className="text-[10px] text-foreground-400 mb-2">想问点什么？</p>
            <div className="flex flex-wrap gap-1.5">
              {quickQuestions.map((q) => (
                <button key={q} onClick={() => handleSend(q)}
                  className="text-[10px] px-2.5 py-1.5 rounded-full bg-background-100 border border-background-200 text-foreground-600 cursor-pointer hover:border-primary-300 transition-colors">{q}</button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg) => (
          <div key={msg.id} className={`flex gap-2 mb-3 ${msg.role === "user" ? "flex-row-reverse" : ""}`}>
            {msg.role === "ai" && (
              <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center shrink-0 mt-0.5"><i className="ri-robot-2-fill text-primary-500 text-[10px]" /></div>
            )}
            <div className={`max-w-[82%] ${msg.role === "user" ? "" : ""}`}>
              <div className={`px-3 py-2 rounded-2xl ${msg.role === "user" ? "bg-primary-500 text-white rounded-tr-md" : "bg-background-100 text-foreground-700 rounded-tl-md"}`}>
                <p className="text-[11px]">{msg.role === "user" ? msg.content : (stripActions(msg.content) || (loading && !msg.content ? <span className="inline-flex gap-0.5"><span className="w-1 h-1 rounded-full bg-foreground-400 animate-bounce" /><span className="w-1 h-1 rounded-full bg-foreground-400 animate-bounce" style={{animationDelay:"150ms"}} /><span className="w-1 h-1 rounded-full bg-foreground-400 animate-bounce" style={{animationDelay:"300ms"}} /></span> : ""))}</p>
              </div>
              {msg.role === "ai" && <ActionButtons text={msg.content} intent={latestIntent} context={messages.map(m => m.content).join(" ")} />}
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {/* Input — fixed at bottom */}
      <div className="px-4 py-3 border-t border-background-100 shrink-0">
        {voiceListening && (
          <div className="mb-2 flex items-center justify-center gap-2 bg-[#E63946]/5 rounded-full py-1.5 px-3 animate-pulse">
            <span className="w-2 h-2 rounded-full bg-[#E63946] animate-ping" />
            <span className="text-[11px] text-[#E63946] font-medium">正在聆听...</span>
            <span className="text-[10px] text-foreground-400">点击停止</span>
          </div>
        )}
        <div className="flex items-center gap-2">
          {voiceSupported && (
            <button onClick={handleVoiceToggle}
              className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 cursor-pointer transition-all ${
                voiceListening
                  ? "bg-[#E63946] text-white animate-pulse"
                  : "bg-background-100 text-foreground-500 hover:bg-background-200"
              }`}
              aria-label={voiceListening ? "停止语音" : "语音输入"}>
              <i className={`${voiceListening ? "ri-mic-fill" : "ri-mic-line"} text-base`} />
            </button>
          )}
          <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleSend()}
            placeholder={voiceListening ? "语音识别中..." : "输入问题..."} disabled={loading}
            className="flex-1 bg-background-100 rounded-full px-3.5 py-2.5 text-[13px] text-foreground-950 placeholder:text-foreground-300 outline-none border-none" />
          <button onClick={() => handleSend()} disabled={!input.trim() || loading}
            className={`w-9 h-9 rounded-full flex items-center justify-center cursor-pointer transition-all disabled:opacity-40 ${input.trim() && !loading ? "bg-primary-500 text-white" : "bg-background-100 text-foreground-300"}`}>
            <i className="ri-send-plane-fill text-sm" />
          </button>
        </div>
      </div>

      {/* Sidebar */}
      {showSidebar && (
        <div className="fixed inset-0 z-50" onClick={() => setShowSidebar(false)}>
          <div className="absolute inset-0 bg-black/40" />
          <div className="absolute left-0 top-0 bottom-0 w-[280px] bg-white shadow-xl flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 pt-4 pb-3 border-b border-background-100 flex items-center justify-between">
              <h2 className="text-sm font-bold text-foreground-950">历史对话</h2>
              <button onClick={newConversation}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-primary-500 text-white text-[10px] font-medium cursor-pointer">
                <i className="ri-add-line text-xs" />新建
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {conversations.length === 0 ? (
                <div className="p-8 text-center">
                  <p className="text-xs text-foreground-400">暂无历史对话</p>
                </div>
              ) : (
                conversations.map((conv) => (
                  <div key={conv.id}
                    className={`flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-background-50 transition-colors border-b border-background-50 ${conv.id === conversationId ? "bg-primary-50" : ""}`}
                    onClick={() => selectConversation(conv)}>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-foreground-950 truncate">{conv.title || "新对话"}</p>
                      <p className="text-[10px] text-foreground-400 mt-0.5">{new Date(conv.createdAt * 1000).toLocaleDateString()}</p>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); handleDelete(conv.id); }}
                      className="w-6 h-6 rounded-full flex items-center justify-center text-foreground-300 hover:text-red-500 hover:bg-red-50 transition-colors shrink-0 ml-2">
                      <i className="ri-delete-bin-line text-xs" />
                    </button>
                  </div>
                ))
              )}
            </div>
            <div className="px-4 py-3 border-t border-background-100">
              <button onClick={newConversation}
                className="w-full py-2 rounded-lg text-xs font-medium bg-background-100 text-foreground-600 cursor-pointer hover:bg-background-200 transition-colors text-center">
                + 开启新对话
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
